import * as Sentry from '@sentry/react'
import { getContextForSentry } from '@/utils/sentryContext'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogContext {
  userId?: string
  component?: string
  action?: string
  metadata?: Record<string, unknown>
  tags?: Record<string, string>
}

/**
 * Logger class with Sentry integration
 * Provides structured logging with automatic Sentry capture for warn/error levels
 */
class Logger {
  private isProd = import.meta.env.VITE_APP_ENV === 'production'

  /**
   * Capture log to Sentry with context
   */
  private captureToSentry(
    level: LogLevel,
    message: string,
    error?: Error,
    context?: LogContext
  ) {
    // Map log level to Sentry severity
    const sentryLevel = level === 'debug' || level === 'info' ? 'info' : level

    Sentry.withScope((scope) => {
      scope.setLevel(sentryLevel)

      // Get global context (userId)
      const globalContext = getContextForSentry()

      // Set user context
      const userId = context?.userId || globalContext.userId
      if (userId) {
        scope.setUser({ id: userId })
      }

      // Set component as tag
      if (context?.component) {
        scope.setTag('component', context.component)
      }

      // Set action as tag
      if (context?.action) {
        scope.setTag('action', context.action)
      }

      // Set custom tags
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value)
        })
      }

      // Set metadata as extra context
      if (context?.metadata) {
        scope.setContext('metadata', context.metadata)
      }

      // Capture to Sentry
      if (error) {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(message, sentryLevel)
      }
    })
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const parts: string[] = [`[${level.toUpperCase()}]`]

    if (context?.component) {
      parts.push(`[${context.component}]`)
    }

    if (context?.action) {
      parts.push(`[${context.action}]`)
    }

    parts.push(message)

    return parts.join(' ')
  }

  /**
   * Log debug message (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.isProd) {
      const formatted = this.formatMessage('debug', message, context)
      console.debug(formatted, context?.metadata || '')
    }
  }

  /**
   * Log info message
   */
  info(message: string, context?: LogContext): void {
    const formatted = this.formatMessage('info', message, context)
    console.info(formatted, context?.metadata || '')
  }

  /**
   * Log warning message and send to Sentry
   */
  warn(message: string, context?: LogContext): void {
    const formatted = this.formatMessage('warn', message, context)
    console.warn(formatted, context?.metadata || '')

    // Send to Sentry
    this.captureToSentry('warn', message, undefined, context)
  }

  /**
   * Log error message and send to Sentry
   */
  error(message: string, error?: any, context?: LogContext): void {
    const formatted = this.formatMessage('error', message, context)

    // Convert error to Error object if needed
    let errorObj: Error | undefined
    if (error instanceof Error) {
      errorObj = error
    } else if (error) {
      errorObj = new Error(String(error))
    }

    console.error(formatted, errorObj || '', context?.metadata || '')

    // Send to Sentry
    this.captureToSentry('error', message, errorObj, context)
  }

  /**
   * Create a context logger with default context
   * Useful for component-specific logging
   */
  createContextLogger(defaultContext: LogContext) {
    return {
      debug: (message: string, additionalContext?: LogContext) =>
        this.debug(message, { ...defaultContext, ...additionalContext }),

      info: (message: string, additionalContext?: LogContext) =>
        this.info(message, { ...defaultContext, ...additionalContext }),

      warn: (message: string, additionalContext?: LogContext) =>
        this.warn(message, { ...defaultContext, ...additionalContext }),

      error: (message: string, error?: any, additionalContext?: LogContext) =>
        this.error(message, error, { ...defaultContext, ...additionalContext }),
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export context logger factory for convenience
export const createLogger = (defaultContext: LogContext) =>
  logger.createContextLogger(defaultContext)
